import * as actions from 'redux/actions/competitors.actions'
import * as accountActions from 'redux/actions/account.actions'
import * as domainsDataActions from 'redux/actions/domainsData.actions'
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import agent, { mocked } from 'agent'

import appMode from 'helpers/getAppMode'
import { exportSaga } from './export.sagas'
import { loadDomainOverview } from 'redux/actions/domainsList.actions'

function* getCompetitorHistory(params) {
  try {
    const { domainId, keywordId } = params.payload
    const state = yield select()
    const filters = state.filters
    const request = appMode.share ? agent.Share.Competitors.history : agent.Competitors.history
    const body = yield call(request, domainId, keywordId, filters)
    const chart = body.data.map(item => ({
      date: item.attributes.date,
      ...item.attributes.position
    }))
    yield put(actions.getCompetitorHistorySuccess(domainId, keywordId, { chart: chart }))
  } catch (error) {
    yield put(actions.getCompetitorHistoryFailure(error))
  }
}

function* deleteCompetitorSaga(params) {
  try {
    const { domainId, competitorId } = params.payload
    const data = yield call(agent.Competitors.del, domainId, competitorId)
    yield put(domainsDataActions.loadCompetitors(domainId, { device: 'desktop' }))
    yield put(actions.deleteCompetitorSuccess(data))
    yield put(accountActions.getAccountData())
    yield put(loadDomainOverview(domainId))
  } catch (error) {
    yield put(actions.deleteCompetitorFailure(error))
  }
}

function* exportKeywordCompetitorsExcelSaga(params) {
  yield call(exportSaga, {
    params: params.payload,
    type: 'xls',
    request: agent.Keywords.exportCompetitorsExcel,
    requestShared: agent.Share.exportShareCompetitorsExcel
  })
}

function* exportKeywordCompetitorsCSVSaga(params) {
  yield call(exportSaga, {
    params: params.payload,
    type: 'csv',
    request: agent.Keywords.exportCompetitorsCSV,
    requestShared: agent.Share.exportShareCompetitorsCSV
  })
}

function* loadCompetitorOverviewSaga(params) {
  try {
    const { domainId, filters } = params.payload
    const request = appMode.share ? agent.Share.Competitors.overview : agent.Competitors.overview
    const response = yield call(request, domainId, filters)
    const competitors = response.data
    const [
      visibilityChart,
      averageRankChart,
      trafficChart,
      serpFeaturesChart,
      serpFeatures,
      overviewChart
    ] = yield all([
      call(getCompetitorsVisibility, domainId, filters),
      call(getCompetitorsAverageRankChart, domainId, filters),
      call(getCompetitorsTraffic, domainId, filters),
      call(getCompetitorsSerpFeaturesChart, domainId, filters),
      call(getCompetitorsSerpFeatures, domainId, filters),
      call(getCompetitorsPosition, domainId, filters)
    ])
    const overview = {
      visibilityChart,
      averageRankChart,
      trafficChart,
      serpFeaturesChart,
      overviewChart,
      overview: competitors.map(item => {
        const data = {
          id: item.id,
          ...item.attributes,
          ...item.attributes.positions,
          ...item.attributes.serpFeatures
        }
        delete data.positions
        delete data.serpFeatures
        return data
      }),
      serpFeatures
    }

    yield put(actions.loadCompetitorOverviewSuccess(overview))
  } catch (error) {
    yield put(actions.loadCompetitorOverviewFailure(error))
  }
}

export function* getCompetitorsVisibility(domainId, filters) {
  const request = appMode.share
    ? agent.Share.Competitors.visibility
    : appMode.preview
    ? mocked.Domains.visibility
    : agent.Competitors.visibility

  const visibility = yield call(request, domainId, filters)
  const visibilityChart = visibility.data.map(item => ({
    date: item.attributes.date,
    ...item.attributes.visibility
  }))
  return visibilityChart
}

export function* getCompetitorsOverview(domainId) {
  const overview = yield call(agent.Domains.overview, domainId)
  return overview.data
}

export function* getCompetitorsAverageRankChart(domainId, filters) {
  const request = appMode.share
    ? agent.Share.Competitors.averagePosition
    : appMode.preview
    ? mocked.Domains.averagePosition
    : agent.Competitors.averagePosition
  const averagePosition = yield call(request, domainId, filters)
  const averageRankChart = averagePosition.data.map(item => ({
    date: item.attributes.date,
    ...item.attributes.averagePosition
  }))
  return averageRankChart
}

export function* getCompetitorsTraffic(domainId, filters) {
  const request = appMode.share
    ? agent.Share.Competitors.traffic
    : appMode.preview
    ? mocked.Domains.traffic
    : agent.Competitors.traffic
  const traffic = yield call(request, domainId, filters)
  const trafficChart = traffic.data.map(item => ({
    date: item.attributes.date,
    ...item.attributes.traffic
  }))
  return trafficChart
}

export function* getCompetitorsPosition(domainId, filters) {
  const getCompetitor = entry => {
    return item => item.competitor === entry.competitor
  }
  const request = appMode.share
    ? agent.Share.Competitors.position
    : appMode.preview
    ? mocked.Domains.position
    : agent.Competitors.position
  const position = yield call(request, domainId, filters)
  let data = {
    oneToThree: [],
    fourToTen: [],
    elevenToFifty: [],
    fiftyOneToOneHundred: [],
    unranked: []
  }

  let item = null
  for (item of position.data) {
    const date = item.attributes.date

    let key = null
    let value = null
    for ([key, value] of Object.entries(item.attributes)) {
      let entry = null
      if (Array.isArray(value)) {
        for (entry of value) {
          const competitor = data[key]?.find(getCompetitor(entry))
          if (!competitor && typeof data[key] !== 'undefined') {
            data[key].push({
              competitor: entry.competitor,
              id: entry.uuid,
              chart: [
                {
                  date: date,
                  value: entry.value
                }
              ]
            })
          } else {
            competitor.chart.push({
              date: date,
              value: entry.value
            })
          }
        }
      }
    }
  }
  return data
}

export function* getCompetitorsSerpFeaturesChart(domainId, filters) {
  const request = appMode.share
    ? agent.Share.Competitors.serpFeatureChart
    : appMode.preview
    ? mocked.Domains.serpFeatureChart
    : agent.Competitors.serpFeatureChart
  const serpFeature = yield call(request, domainId, filters)
  const serpFeatureChart = serpFeature.data.map(item => ({
    date: item.attributes.date,
    ...item.attributes.ownedSerpFeatures
  }))
  return serpFeatureChart
}

export function* getCompetitorsSerpFeatures(domainId, filters) {
  const request = appMode.share
    ? agent.Share.Competitors.serpFeature
    : appMode.preview
    ? mocked.Domains.serpFeature
    : agent.Competitors.serpFeature
  const serpFeatures = yield call(request, domainId, filters)
  return Object.entries(serpFeatures.data.attributes).map(([key, value]) => ({
    [key]: value
  }))
}

export default function* competitorsSagas() {
  yield all([
    takeEvery(actions.getCompetitorHistory.type, getCompetitorHistory),
    takeLatest(actions.deleteCompetitor.type, deleteCompetitorSaga),
    takeLatest(actions.exportDomainKeywordCompetitorsExcel.type, exportKeywordCompetitorsExcelSaga),
    takeLatest(actions.exportDomainKeywordCompetitorsCSV.type, exportKeywordCompetitorsCSVSaga),
    takeLatest(actions.loadCompetitorOverview.type, loadCompetitorOverviewSaga)
  ])
}
